import { z } from 'zod';
import { cursorPageSchema, cursorQuerySchema, decimalStringSchema, isoDateTimeSchema, localeSchema, uuidSchema, } from './common.js';
import { currencyCodeSchema, matchConfidenceSchema, moneySchema, priceChangeEntrySchema, signedMoneySchema, } from './prices.js';
/**
 * Receipts (docs/partial/shopping-in-store-spec.md 5.4, 6.5): photos of one purchase, read in
 * the background, reviewed line by line, confirmed into price observations. Payment, loyalty,
 * tax and staff data are never part of a receipt.
 */
export const receiptStatusSchema = z.enum(['PROCESSING', 'REVIEW', 'CONFIRMED', 'FAILED']);
export const receiptLineKindSchema = z.enum(['ITEM', 'DEPOSIT', 'DISCOUNT', 'RETURN', 'OTHER']);
export const receiptMatchStatusSchema = z.enum(['PROPOSED', 'CONFIRMED', 'IGNORED', 'UNMATCHED']);
export const receiptCreateSchema = z.object({
    /** Client-generated: photos queued offline create the receipt exactly once. */
    id: uuidSchema.optional(),
    /** In reading order, top part first. */
    assetIds: z.array(uuidSchema).min(1).max(12),
    shoppingListId: uuidSchema.nullable().optional(),
    storeId: uuidSchema.nullable().optional(),
    language: localeSchema.optional(),
});
/** Input stored on the AIGeneration row for RECEIPT_EXTRACTION. */
export const aiReceiptInputSchema = z.object({
    receiptId: uuidSchema,
    language: localeSchema.default('de'),
});
/** What the generation reports; the lines themselves live on the receipt. */
export const aiReceiptResultSchema = z.object({
    receiptId: uuidSchema,
    lines: z.number().int(),
    droppedLines: z.number().int(),
    sumMismatch: z.boolean(),
});
export const receiptLineSchema = z.object({
    id: uuidSchema,
    position: z.number().int(),
    rawText: z.string(),
    kind: receiptLineKindSchema,
    quantity: decimalStringSchema.nullable(),
    unitPrice: moneySchema.nullable(),
    totalPrice: signedMoneySchema,
    weight: decimalStringSchema.nullable(),
    weightUnitCode: z.string().nullable(),
    /** Deposit, discount or return: the line it belongs to. */
    refersToLineId: uuidSchema.nullable(),
    product: z.object({ id: uuidSchema, name: z.string(), brand: z.string().nullable() }).nullable(),
    ingredient: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    shoppingListItem: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    matchConfidence: matchConfidenceSchema.nullable(),
    matchStatus: receiptMatchStatusSchema,
    /** Why the match was proposed; null once the user decided. */
    matchVia: z.enum(['ALIAS', 'ITEM_PRODUCT', 'ITEM_NAME', 'CHAIN_PRODUCT']).nullable(),
});
export const receiptSummarySchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    status: receiptStatusSchema,
    store: z.object({ id: uuidSchema, name: z.string() }).nullable(),
    storeNameRaw: z.string().nullable(),
    purchasedAt: isoDateTimeSchema.nullable(),
    total: moneySchema.nullable(),
    currency: currencyCodeSchema.nullable(),
    shoppingListId: uuidSchema.nullable(),
    generationId: uuidSchema.nullable(),
    errorCode: z.string().nullable(),
    /** The same receipt was uploaded before. */
    duplicateOfId: uuidSchema.nullable(),
    sumMismatch: z.boolean(),
    lineCount: z.number().int(),
    matchedLineCount: z.number().int(),
    hasImages: z.boolean(),
    imagesExpireAt: isoDateTimeSchema.nullable(),
    /** The reader uploaded it: may delete the photos and the receipt. */
    mine: z.boolean(),
    editable: z.boolean(),
    confirmedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const receiptPageSchema = cursorPageSchema(receiptSummarySchema);
export const receiptListQuerySchema = cursorQuerySchema.extend({
    status: receiptStatusSchema.optional(),
});
export const receiptSchema = receiptSummarySchema.extend({
    storeAddress: z.string().nullable(),
    lines: z.array(receiptLineSchema),
    images: z.array(z.object({ assetId: uuidSchema, url: z.string().nullable() })),
    /** What the list estimate said when the receipt was confirmed (spec 5.6). */
    estimatedTotal: moneySchema.nullable(),
});
export const receiptUpdateSchema = z.object({
    storeId: uuidSchema.nullable().optional(),
    purchasedAt: isoDateTimeSchema.nullable().optional(),
    total: moneySchema.nullable().optional(),
    currency: currencyCodeSchema.optional(),
    shoppingListId: uuidSchema.nullable().optional(),
});
export const receiptLineUpdateSchema = z.object({
    /** Setting a target confirms the match; `null` for all three = unmatched. */
    productId: uuidSchema.nullable().optional(),
    ingredientId: uuidSchema.nullable().optional(),
    shoppingListItemId: uuidSchema.nullable().optional(),
    /** "Ignore": the line keeps its text and price and produces nothing. */
    ignored: z.boolean().optional(),
    kind: receiptLineKindSchema.optional(),
    quantity: decimalStringSchema.nullable().optional(),
    unitPrice: moneySchema.nullable().optional(),
    totalPrice: signedMoneySchema.optional(),
    refersToLineId: uuidSchema.nullable().optional(),
});
export const receiptConfirmResponseSchema = z.object({
    receipt: receiptSchema,
    observations: z.number().int(),
    itemsPriced: z.number().int(),
    aliasesLearned: z.number().int(),
    /** Products that became noticeably more expensive with this receipt (spec 5.7). */
    increases: z.array(priceChangeEntrySchema),
});
//# sourceMappingURL=receipts.js.map